import React from 'react';
import {IExternalLinkRenderer} from "./external-link-renderer.interface";
import {ExternalLinkIframeComponent} from "../../../components/external-links/external-link-iframe.component";
import {IServiceFactoryExtended} from "../../service-factory-extended.interface";
import {IModalDialogHandler} from "../../dialog/dialog.service.interface";
import {observable, runInAction} from "mobx";
import {NullableNumber} from "../../../../common/types/nullable-types";
import {ExternalLinksZIndexes} from "../external-links-z-indexes";

export interface ExternalLinkIframeRendererOptions {
    maxWidth: number;
    maxHeight: number;
    zIndex: number;
    shouldBeClosedOnlyByUser?: boolean;
    onClosed: () => Promise<void>;
    hideCloseButton: boolean;
    analyticsAction: string;
    httpVerb: 'GET' | 'POST';
    createAsHidden?: boolean;
    iframeScrolling?: "auto" | "no";
}

export class ExternalLinkIframeRenderer implements IExternalLinkRenderer {
    constructor(private readonly services: IServiceFactoryExtended,
                private readonly _options: ExternalLinkIframeRendererOptions) {
        this._id = services.tools.getGuid();
    }
    dispose(): void {
        this._dialogHandler?.close();
    }

    display(): void {
        this._dialogHandler?.display();
    }

    @observable.ref
    private _customMaxWidth: NullableNumber = null;
    @observable.ref
    private _customMaxHeight: NullableNumber = null;
    setMaxSize(maxWidth: number, maxHeight: number): void {
        runInAction(() => {
            this._customMaxWidth = maxWidth;
            this._customMaxHeight = maxHeight;
        });
    }

    getMaxWidth = (): number => {
        return this._customMaxWidth ?? this._options.maxWidth;
    }
    getMaxHeight = (): number => {
        return this._customMaxHeight ?? this._options.maxHeight;
    }


    private readonly _id: string;

    private _dialogHandler: IModalDialogHandler | null = null;
    @observable.ref
    iframeElementRef: HTMLIFrameElement | null = null;



    private _formPostInIframe(iframeElement: HTMLIFrameElement | null, url: string): void {
        if(!iframeElement) {
            return;
        }

        const formElement: HTMLFormElement = document.createElement('form');
        formElement.target = iframeElement.name;
        const urlBuilder = new URL(url);
        urlBuilder.searchParams.forEach((paramValue, paramName) => {
            const formField = document.createElement('input');
            formField.setAttribute('type', 'hidden');
            formField.setAttribute('name', paramName);
            formField.setAttribute('value', paramValue);
            formElement.appendChild(formField);
        });

        urlBuilder.search = "";
        urlBuilder.hash = "";
        formElement.setAttribute('method', 'post');
        formElement.setAttribute('action', urlBuilder.href);

        this.services.dom.body.appendChild(formElement);
        formElement.submit();
        formElement.remove();

    }

    render(url: string): Promise<void> {
        return new Promise<void>((resolve) => {

            const handleIframeCreation = (iframeElement: HTMLIFrameElement | null) => {
                runInAction(() => {
                    this.iframeElementRef = iframeElement;
                });

                if(this._options.httpVerb === 'POST') {
                    this._formPostInIframe(iframeElement, url);
                }
            }

            this.services.dialog.showModal((dialogHandler) => {
                this._dialogHandler = dialogHandler;
                return (
                    <ExternalLinkIframeComponent id={this._id}
                                                 url={this._options.httpVerb === 'POST' ? '' : url}
                                                 maxWidth={this.getMaxWidth}
                                                 maxHeight={this.getMaxHeight}
                                                 hideCloseButton={this._options.hideCloseButton}
                                                 onIframeCreated={handleIframeCreation}
                                                 dialogHandler={dialogHandler}
                                                 scrolling={this._options.iframeScrolling}/>
                )
            }, {
                disableFullscreenMobile: false,
                hideCloseButton: true,
                noDefaultContentPadding: true,
                isHighPriority: true,
                hideBorders: this.services.mediaQuery.lAndBelow,
                forceZOrder: this._options.zIndex,
                shouldBeClosedOnlyByUser: this._options.shouldBeClosedOnlyByUser,
                overflow: 'unset',
                createAsHidden: this._options.createAsHidden,
                onClosedByXButton: () => {
                    this.services.analytics.sendTechnicalEvent(this._options.analyticsAction, 'CloseBtnClicked');
                },
                onClosed: () => {
                    this._options.onClosed().then(() => resolve());
                }
            });
        });
    }


    updateZIndex(zIndex: ExternalLinksZIndexes): void {
        this._dialogHandler.zIndex = zIndex;
    }


}